import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useTracking } from "react-tracking";

import { startLoadingFlowEngineCatalogs } from "../../../app/actions/flowEngineActions";
import { startLoadingRuleFlowsByRoleAndModule } from "../../../app/actions/ruleFlowActions";
import {
  previewRoleFlowBatchAssignments,
  roleFlowStartLoadingByModuleAndReference,
  roleFlowStartRegister
} from "../../../app/actions/roleFlowActions";
import { isFlowEngineEnabledForProcess, isModalReasonCatalogEnabled, isNoteForwardEnabled, isExpertConsultEnabledForProcess, isCoordinatorApprovalEnabled } from "../../../app/helpers/flowEngineConfigHelper";
import {
  filterVisibleFlowActions,
  hasActiveLicenseRules,
  hasCompletedLicenseVerification,
  isTrayLeavingFlowAction
} from "../../../app/helpers/flowActionVisibilityHelper";
import { getRoleDisplayLabel } from "../../../app/helpers/specialistRoleModuleHelper";
import { normalizeSpecialistRoleKey, resolveModuleAccessForRole } from "../../../app/helpers/activeSpecialistRoleHelper";
import useActiveSpecialistRole from "../../../app/hooks/useActiveSpecialistRole";
import { fetchFlowHistoryByRecord, fetchFlowHistoriesByRecords } from "../../../app/actions/flowHistoryActions";
import { resolveMassVisibleActions, summarizeMassSelection } from "../../../app/helpers/flowMassSelectionHelper";
import {
  findLastModificationByRole,
  formatDiffEntriesForDisplay,
  hasRecordMotivo,
  resolveRecordMotivo,
  resolveRoleLabel,
  resolveUserLabel
} from "../../../app/helpers/flowHistoryDisplayHelper";
import {
  buildCatalogLookups,
  buildModuleFieldLabel,
  buildUsersByIdFromEvents,
  canOperateFlowRecord,
  isFlowRecordInAssignmentPool,
  isFlowRecordPublished,
  resolveAuthUserId
} from "../../../app/helpers/flowEditPageHelper";
import { resolveFlowStateSemaphore, getSemaphoreTitle } from "../../../app/helpers/flowStateSemaphoreHelper";
import { getCasePhaseMeta, isDriDocumentPhase, resolveCasePhase } from "../../../app/helpers/flowCasePhaseHelper";
import { formatAssignmentLine, shouldRevealAssignee } from "../../../app/helpers/flowAssignmentRevealHelper";
import { resolveGenerationPhaseLabel } from "../../../app/helpers/flowEditPageTitleHelper";

import FlowActionButtons from "./FlowActionButtons";
import FlowActionModal from "./FlowActionModal";
import FlowRecordHistoryModal from "./FlowRecordHistoryModal";
import FlowStateSemaphore from "./FlowStateSemaphore";
import FlowChecklistPanel from "./FlowChecklistPanel";
import { debugLogPages } from "../../../app/helpers/debugHelper";

const FlowEnginePanel = ({
  record,
  moduleId,
  moduleProcess,
  moduleName,
  roleId,
  roleCode,
  roleName,
  canOperate = true,
  selectedRecords = [],
  onAfterAction,
  layoutMode = "list",
  showActions = true,
  showSummary = false,
  showHistoryButton = true,
  compact = false,
  poolSurface = "",
  activeCatalogRoleId = null,
  activeRoleKey = "",
  subjectKind = ""
}) => {
  debugLogPages(":: FlowEnginePanel", {
    record,
    moduleProcess,
    moduleId,
    roleId,
    roleCode,
    selectedRecords,
    layoutMode
  });

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const configuration = useSelector((state) => state.configuration);
  const flowEngine = useSelector((state) => state.flowEngine);
  const ruleFlow = useSelector((state) => state.ruleFlow);
  const auth = useSelector((state) => state.auth);
  const roleLogin = useSelector((state) => state.userRole?.roleLogin || {});
  const roles = roleLogin.roles || [];
  const catalog = useSelector((state) => state.catalog);
  const catalogCiiu = useSelector((state) => state.catalogCiiu);
  const catalogSection = useSelector((state) => state.catalogSection);
  const catalogPhase = useSelector((state) => state.catalogPhase);
  const locationCountry = useSelector((state) => state.locationCountry);
  const catalogRoles = useSelector((state) => state.catalogRole?.catalogRoles || []);

  const [selectedAction, setSelectedAction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historiesById, setHistoriesById] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [assignmentResultText, setAssignmentResultText] = useState("");
  const [massPreviewText, setMassPreviewText] = useState("");
  const [massPreviewLoading, setMassPreviewLoading] = useState(false);

  const engineEnabled = isFlowEngineEnabledForProcess(configuration, moduleProcess);
  const modalReasonIdEnabled = isModalReasonCatalogEnabled(configuration);
  const noteForwardEnabled = isNoteForwardEnabled(configuration);
  const expertConsultEnabled = isExpertConsultEnabledForProcess(configuration, moduleProcess);
  const coordinatorApprovalEnabled = isCoordinatorApprovalEnabled(configuration);
  const roleKeyFromName = normalizeSpecialistRoleKey({ name: roleName, code: roleCode });
  const { roleKey: selectedRoleKey, selectedRole } = useActiveSpecialistRole();
  const termFeeder = subjectKind === "search_key_term" && selectedRoleKey === "feeder";
  const roleKey = termFeeder ? "feeder" : roleKeyFromName;
  const localizedRoleName = getRoleDisplayLabel(
    termFeeder ? "Specialist-Feeder" : roleName,
    t
  );
  const generationPhase = resolveGenerationPhaseLabel(selectedRoleKey || roleKey, t);
  const selectedMatchesTray = !selectedRoleKey
    || selectedRoleKey === roleKey
    || selectedRoleKey === "coordinator"
    || termFeeder;
  const allowedProcesses = useMemo(() => {
    let roleModules = selectedRole?.moduleAccess || roleLogin?.moduleAccess;
    if ((!Array.isArray(roleModules) || !roleModules.length) && selectedRole?.id) {
      roleModules = resolveModuleAccessForRole(roleLogin?.moduleAccessByRole, selectedRole);
    }
    return (roleModules || []).map((item) => String(item).toLowerCase());
  }, [roleLogin?.moduleAccess, roleLogin?.moduleAccessByRole, selectedRole]);

  const massTargets = useMemo(() => {
    if (selectedRecords.length > 0) {
      return selectedRecords.map((item) => (
        typeof item === "object" && item !== null ? item.id : item
      ));
    }
    if (record?.id) {
      return [record.id];
    }
    return [];
  }, [selectedRecords, record]);

  const multipleSelected = selectedRecords.length > 1;
  const showSingleRecordHint = layoutMode === "list" && multipleSelected;
  const massTargetKey = massTargets.join(',');

  useEffect(() => {
    if (engineEnabled && !flowEngine.loaded && !flowEngine.loading) {
      dispatch(startLoadingFlowEngineCatalogs());
    }
  }, [dispatch, engineEnabled, flowEngine.loaded, flowEngine.loading]);

  useEffect(() => {
    if (!engineEnabled || !record?.id || multipleSelected) {
      return;
    }
    dispatch(roleFlowStartLoadingByModuleAndReference(moduleId, record.id));
  }, [dispatch, engineEnabled, record, moduleId, multipleSelected]);

  useEffect(() => {
    if (!engineEnabled || !roleId || !moduleId) {
      return;
    }
    dispatch(startLoadingRuleFlowsByRoleAndModule(roleId, moduleId, {
      reference_id: record?.id,
      process: moduleProcess,
      subject_kind: subjectKind || undefined
    }));
  }, [dispatch, engineEnabled, roleId, moduleId, record?.id, moduleProcess, subjectKind]);

  useEffect(() => {
    if (!engineEnabled || !moduleId || !massTargets.length) {
      setHistoryEvents([]);
      setHistoriesById({});
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      try {
        const rows = massTargets.length === 1
          ? await fetchFlowHistoryByRecord(moduleId, massTargets[0], subjectKind)
          : await fetchFlowHistoriesByRecords(moduleId, massTargets, subjectKind);
        if (cancelled) {
          return;
        }
        if (massTargets.length === 1) {
          setHistoryEvents(rows);
          setHistoriesById({ [massTargets[0]]: rows });
          return;
        }
        const grouped = (rows || []).reduce((acc, event) => {
          const key = Number(event.reference_id);
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(event);
          return acc;
        }, {});
        setHistoriesById(grouped);
        setHistoryEvents(grouped[Number(record?.id)] || []);
      } catch {
        if (!cancelled) {
          setHistoryEvents([]);
          setHistoriesById({});
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [engineEnabled, moduleId, massTargetKey, record?.id, subjectKind]);

  const licenseCompleted = useMemo(
    () => hasCompletedLicenseVerification(historyEvents),
    [historyEvents]
  );

  const casePhase = useMemo(() => {
    const recordRole = catalogRoles.find(
      (role) => Number(role.id) === Number(record?.current_role_catalog_id)
    );
    return resolveCasePhase(record, {
      roleName: recordRole?.name,
      process: moduleProcess,
      licenseCompleted
    });
  }, [catalogRoles, licenseCompleted, moduleProcess, record]);
  const casePhaseMeta = getCasePhaseMeta(casePhase);

  const selectedRecordObjects = useMemo(() => (
    selectedRecords.length
      ? selectedRecords.filter((item) => item && typeof item === 'object')
      : (record ? [record] : [])
  ), [selectedRecords, record]);

  const mappedActions = useMemo(() => {
    const rules = Array.isArray(ruleFlow?.ruleFlows) ? ruleFlow.ruleFlows : [];
    return rules
      .filter((rule) => !rule.deleted)
      .map((rule) => ({
        id: rule.id,
        value: rule.value,
        description: rule.description,
        key_description: rule.key_description,
        key_question: rule.key_question,
        flow_id: rule.flow_id,
        requires_note: rule.requires_note,
        pending_reason_required: rule.pending_reason_required,
        cancel_reason_required: rule.cancel_reason_required,
        target_state_catalog_id: rule.target_state_catalog_id,
        target_flow_id: rule.target_flow_id,
        assignment_presentation: rule.assignment_presentation || null
      }));
  }, [ruleFlow]);

  const licenseEnabled = useMemo(
    () => hasActiveLicenseRules(mappedActions),
    [mappedActions]
  );

  const massSummary = useMemo(() => {
    if (!multipleSelected) {
      return { mixed: false, total: selectedRecordObjects.length, groups: [], phase: null };
    }
    return summarizeMassSelection({
      records: selectedRecordObjects,
      flowStates: flowEngine.states,
      historiesById,
      roleKey,
      moduleProcess,
      licenseEnabled,
      t
    });
  }, [multipleSelected, selectedRecordObjects, flowEngine.states, historiesById, roleKey, moduleProcess, licenseEnabled, t]);

  const recordPublished = isFlowRecordPublished(record, flowEngine);
  const recordInAssignmentPool = isFlowRecordInAssignmentPool(record);
  const recordInCurrentBandeja = useMemo(() => {
    if (recordPublished && (roleKey === "coordinator" || roleKey === "liaison")) {
      return true;
    }
    if (recordPublished) {
      return false;
    }
    if (recordInAssignmentPool) {
      if (roleKey === 'coordinator' || roleKey === 'liaison') {
        return true;
      }
      return Number(record.current_role_catalog_id) === Number(roleId);
    }
    if (!record?.current_role_catalog_id || !roleId) {
      return true;
    }
    return Number(record.current_role_catalog_id) === Number(roleId);
  }, [record?.current_role_catalog_id, recordInAssignmentPool, roleId, roleKey, recordPublished]);
  const canOperateFlow = canOperateFlowRecord({
    record,
    roleId,
    flowEngineEnabled: engineEnabled,
    flowEngine,
    canOperate: canOperate && selectedMatchesTray,
    roleKey,
    selectedRoleKey,
    actorUserId: resolveAuthUserId(auth?.user),
    allowedProcesses,
    process: moduleProcess || record?.process
  });

  const availableActions = useMemo(() => {
    if (!engineEnabled || !mappedActions.length) {
      return [];
    }

    if (multipleSelected) {
      return resolveMassVisibleActions({
        actions: mappedActions,
        records: selectedRecordObjects,
        flowStates: flowEngine.states,
        historiesById,
        moduleProcess,
        expertConsultEnabled,
        canOperate: canOperateFlow,
        mixed: massSummary.mixed,
        roleKey,
        coordinatorApprovalEnabled,
        subjectKind
      });
    }

    return filterVisibleFlowActions({
      actions: mappedActions,
      record,
      flowStates: flowEngine.states,
      moduleProcess,
      expertConsultEnabled,
      canOperate: canOperateFlow,
      licenseCompleted,
      roleKey,
      coordinatorApprovalEnabled,
      subjectKind
    });
  }, [engineEnabled, mappedActions, record, flowEngine.states, moduleProcess, expertConsultEnabled, canOperateFlow, licenseCompleted, multipleSelected, selectedRecordObjects, historiesById, massSummary.mixed, roleKey, coordinatorApprovalEnabled, subjectKind]);

  useEffect(() => {
    setActionCompleted(false);
    setAssignmentResultText("");
  }, [record?.id, record?.current_role_catalog_id, record?.state_catalog_id, selectedRecords.length]);

  const handleActionClick = (action) => {
    setSelectedAction(action);
    setIsModalOpen(true);
  };

  const revealSelectedAction = shouldRevealAssignee(
    configuration,
    moduleProcess,
    selectedAction?.assignment_presentation || {}
  );

  useEffect(() => {
    if (!isModalOpen || !selectedAction || !revealSelectedAction || !multipleSelected) {
      setMassPreviewText("");
      setMassPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setMassPreviewLoading(true);
    previewRoleFlowBatchAssignments({
      process: moduleProcess,
      catalogmodule_id: moduleId,
      catalogrole_id: roleId,
      ruleflow_id: selectedAction.id,
      reference_ids: massTargets,
      subject_kind: subjectKind || undefined
    }).then((rows) => {
      if (cancelled) {
        return;
      }
      const lines = (rows || []).map((item) => formatAssignmentLine({
        id: item.reference_id,
        code: item.code,
        name: item.assigned_user_name,
        usePool: item.use_pool,
        t,
        subjectKind: item.subject_kind || subjectKind,
        areaKey: item.area_key
      }));
      setMassPreviewText(lines.join(" | "));
    }).finally(() => {
      if (!cancelled) {
        setMassPreviewLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    isModalOpen,
    selectedAction,
    revealSelectedAction,
    multipleSelected,
    moduleProcess,
    moduleId,
    roleId,
    massTargetKey,
    subjectKind,
    t
  ]);

  const handleCloseModal = () => {
    if (isSubmitting) {
      return;
    }
    setIsModalOpen(false);
  };

  const handleModalAfterClose = () => {
    setSelectedAction(null);
  };

  const catalogLookups = useMemo(
    () => buildCatalogLookups({ catalog, catalogCiiu, catalogSection, catalogPhase, locationCountry }),
    [catalog, catalogCiiu, catalogSection, catalogPhase, locationCountry]
  );

  const usersById = useMemo(
    () => buildUsersByIdFromEvents(historyEvents, auth?.user),
    [historyEvents, auth?.user]
  );

  const fieldLabelResolver = useMemo(
    () => (field) => buildModuleFieldLabel(moduleProcess, field, t),
    [moduleProcess, t]
  );

  const actionsDisabled = !massTargets.length || !canOperateFlow || isSubmitting || actionCompleted;

  const handleSubmitAction = async (formValues) => {
    if (massSummary.mixed) {
      return;
    }
    if (!selectedAction || !massTargets.length || !roleId || !canOperateFlow || isSubmitting) {
      handleCloseModal();
      return;
    }

    setIsSubmitting(true);

    trackEvent({
      component: "FlowEnginePanel",
      method: "handleSubmitAction",
      event: "FlowActionTriggered",
      payload: {
        action: selectedAction.value,
        module: moduleName || moduleProcess,
        records: massTargets
      }
    });

    const payload = {
      ...formValues,
      flow_id: selectedAction.flow_id,
      ruleflow_id: selectedAction.id,
      catalogmodule_id: moduleId,
      reference_id: record?.id,
      catalogrole_id: roleId,
      pool_surface: poolSurface,
      active_catalogrole_id: activeCatalogRoleId,
      active_role_key: activeRoleKey,
      process: moduleProcess,
      subject_kind: subjectKind || undefined,
      value: selectedAction.value,
      user_id: resolveAuthUserId(auth?.user),
      mass_operation_flag: multipleSelected
    };

    if (multipleSelected) {
      payload.action_payload = {
        mass_targets: massTargets
      };
      payload.assignment_batch_id = `mass-${crypto.randomUUID()}`;
    }

    try {
      const result = await dispatch(roleFlowStartRegister(payload));
      if (!result?.ok) {
        return;
      }

      const reveal = shouldRevealAssignee(
        configuration,
        moduleProcess,
        selectedAction?.assignment_presentation || {}
      );
      if (reveal && Array.isArray(result.assignmentResults) && result.assignmentResults.length) {
        const lines = result.assignmentResults.map((item) => formatAssignmentLine({
          id: item.reference_id,
          code: item.code,
          name: item.assigned_user_name,
          usePool: item.use_pool,
          t,
          subjectKind: item.subject_kind || subjectKind,
          areaKey: item.area_key
        }));
        setAssignmentResultText(lines.join(" | "));
      } else {
        setAssignmentResultText("");
      }

      if (record?.id && !multipleSelected) {
        dispatch(roleFlowStartLoadingByModuleAndReference(moduleId, record.id));

        try {
          const rows = await fetchFlowHistoryByRecord(moduleId, record.id, subjectKind);
          setHistoryEvents(rows);
        } catch {
          setHistoryEvents([]);
        }
      }

      if (typeof onAfterAction === "function") {
        await onAfterAction();
      }

      if (isTrayLeavingFlowAction(selectedAction)) {
        setActionCompleted(true);
      }
    } finally {
      setIsSubmitting(false);
      handleCloseModal();
    }
  };

  if (!engineEnabled) {
    return null;
  }

  if (!record) {
    return (
      <div className="alert alert-info mt-3">
        {t("general.flow-engine.select-record", "Selecciona un registro para visualizar y operar el flujo.")}
      </div>
    );
  }

  if (!canOperate && !recordPublished) {
    return (
      <div className="alert alert-warning mt-3">
        {t("general.flow-engine.readonly-no-role", "No tienes permisos para operar el flujo en este registro.")}
      </div>
    );
  }

  const stateLabel = flowEngine?.states?.find((s) => s.id === record.state_catalog_id);
  const semaphore = resolveFlowStateSemaphore(record.state_catalog_id, flowEngine.states);
  const recordMotivo = resolveRecordMotivo(record, flowEngine, t);
  const showRecordMotivo = hasRecordMotivo(record, flowEngine);
  const lastModification = findLastModificationByRole(historyEvents, record?.current_role_catalog_id);
  const lastModificationDiffs = lastModification
    ? formatDiffEntriesForDisplay({
      diffSnapshot: lastModification.diff_snapshot,
      fieldLabelResolver,
      flowEngine,
      roles,
      t,
      recordStateCatalogId: record.state_catalog_id,
      usersById,
      catalogLookups
    })
    : [];

  const actionButtons = (
    <FlowActionButtons
      actions={availableActions}
      disabled={actionsDisabled}
      onSelectAction={handleActionClick}
      roleKey={roleKey}
    />
  );

  const publishedNotice = recordPublished && (
    <div className="alert alert-info small mb-3">
      {roleKey === "coordinator" || roleKey === "liaison"
        ? (record?.is_visible === false
          ? t(
            "general.flow-engine.record-published-paused",
            "Registro publicado y no visible para el usuario final. Puede reanudar, dar de baja o guardar con comentario."
          )
          : t(
            "general.flow-engine.record-published-coordinator",
            "Registro publicado. Puede pausar la visibilidad, dar de baja o guardar con comentario. published_at se conserva al pausar."
          ))
        : t(
          "general.flow-engine.record-published-locked",
          "Este registro está publicado y no puede moverse en el flujo."
        )}
    </div>
  );

  const movedBandejaNotice = !recordPublished && !recordInCurrentBandeja && record?.current_role_catalog_id && (
    <div className="alert alert-info small mb-3">
      {t(
        "general.flow-engine.record-moved-bandeja",
        "El registro ya no está en tu bandeja. Consulta el historial para ver su estado actual."
      )}
    </div>
  );

  const massStateNotice = multipleSelected && (
    <div className="mb-3">
      {massSummary.mixed ? (
        <div className="alert alert-warning small mb-0">
          <strong>
            {t(
              'flow.mass.mixed-title',
              'Algunos registros seleccionados se encuentran en diferentes estados'
            )}
          </strong>
          <ul className="mb-0 mt-2">
            {massSummary.groups.map((group) => (
              <li key={group.phase}>
                {group.label}: {group.count}
              </li>
            ))}
          </ul>
          <p className="mb-0 mt-2">
            {t(
              'flow.mass.mixed-block',
              'No se puede aplicar una operación masiva hasta que todos estén en el mismo estado.'
            )}
          </p>
        </div>
      ) : (
        massSummary.groups[0] && (
          <p className="small mb-0">
            {t('flow.mass.uniform', 'Estado común:')} {massSummary.groups[0].label} ({massSummary.groups[0].count})
          </p>
        )
      )}
    </div>
  );

  const actionsBlock = showActions && (
    <div className="mb-3">
      {compact && massStateNotice}
      {!recordPublished && (
        <div className="small text-muted mb-2">
          {t('flow.actions.available', 'Acciones disponibles')}
        </div>
      )}
      {publishedNotice}
      {movedBandejaNotice}
      {record?.id && !recordInAssignmentPool && subjectKind !== "search_key_term" && (
        <FlowChecklistPanel
          record={record}
          moduleId={moduleId}
          moduleProcess={moduleProcess}
          roleId={roleId}
          canOperate={canOperateFlow}
          referenceIds={massTargets}
          poolSurface={poolSurface}
          activeCatalogRoleId={activeCatalogRoleId}
          activeRoleKey={activeRoleKey}
        />
      )}
      {canOperateFlow && !multipleSelected && roleKey === 'manager' && isDriDocumentPhase(casePhase) && (
        <div className="alert alert-warning py-2 small mb-2">
          {t(
            'flow.actions.send-validator-dri-blocked',
            'No se puede enviar a Validador mientras el caso esté en DRI Final. Complete la ficha (Prospecto del Caso) y vuelva a intentar.'
          )}
        </div>
      )}
      {canOperateFlow && actionButtons}
      {assignmentResultText && (
        <p className="small text-primary mt-2 mb-0">{assignmentResultText}</p>
      )}
    </div>
  );

  const summaryBlock = showSummary && (
    <div className="mb-3 p-3 bg-light rounded border">
      {showRecordMotivo && (
        <div className="mb-2">
          <span className="small text-muted">{t('flow.motivo.label', 'Motivo')}:</span>{' '}
          <span>{recordMotivo}</span>
        </div>
      )}
      {lastModification ? (
        <div>
          <div className="small text-muted mb-1">
            {t('general.flow-engine.history.last-changes', 'Últimos cambios del perfil')}
          </div>
          <div className="small mb-1">
            {resolveRoleLabel(lastModification.catalogrole_id, roles, t)} · {resolveUserLabel(lastModification)}
          </div>
          <ul className="list-unstyled small mb-0">
            {lastModificationDiffs.map((entry) => (
              <li key={`${lastModification.id}-${entry.field}`}>
                <strong>{entry.label}:</strong>{' '}
                <span className="text-muted">{String(entry.previous)}</span>
                {' → '}
                <span>{String(entry.next)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="small text-muted mb-0">
          {t('flow.history.no-modifications', 'No existen modificaciones en el registro.')}
        </p>
      )}
    </div>
  );

  const actionModal = (
    <FlowActionModal
      isOpen={isModalOpen}
      action={selectedAction}
      onClose={handleCloseModal}
      onAfterClose={handleModalAfterClose}
      onSubmit={handleSubmitAction}
      modalReasonIdEnabled={modalReasonIdEnabled}
      noteForwardEnabled={noteForwardEnabled}
      pendingReasons={flowEngine.pendingReasons}
      cancelReasons={flowEngine.cancelReasons}
      normalReasons={flowEngine.normalReasons}
      ownerApprovals={flowEngine.ownerApprovals}
      isSubmitting={isSubmitting}
      massCount={massTargets.length}
      massSummary={massSummary}
      revealAssignee={revealSelectedAction}
      record={record}
      massDestinationPreview={massPreviewText}
      massPreviewLoading={massPreviewLoading}
    />
  );

  if (compact) {
    return (
      <div className="mb-3 mx-2 mx-md-4 p-3 border rounded bg-light">
        {actionsBlock}
        {actionModal}
      </div>
    );
  }

  return (
    <div className="card mt-3 mx-2 mx-md-4 border-primary-subtle">
      <div className="card-header py-2 d-flex justify-content-between align-items-center">
        <div>
          <strong>{t("general.flow-engine.panel-title", "Motor de flujos")}</strong>
          <span className="text-muted ms-2 small">
            {generationPhase
              ? `${localizedRoleName}. ${generationPhase}`
              : `${localizedRoleName} · ${moduleName || moduleProcess}`}
          </span>
        </div>
        {showHistoryButton && !multipleSelected && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
            onClick={() => setIsHistoryOpen(true)}
            title={t("general.flow-engine.history.open", "Ver historial")}
          >
            <FlowStateSemaphore stateCatalogId={record.state_catalog_id} interactive={false} />
            <i className="fa fa-history" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className="card-body">
        {!showSummary && !multipleSelected && (
          <p className="small text-muted mb-2 d-flex align-items-center gap-2">
            <FlowStateSemaphore stateCatalogId={record.state_catalog_id} onClick={() => setIsHistoryOpen(true)} />
            {t("general.flow-engine.current-state", "Estado actual")}: {" "}
            <strong>{stateLabel?.name || getSemaphoreTitle(semaphore, t)}</strong>
            <span className={`badge bg-${subjectKind === "search_key_term" ? "warning" : casePhaseMeta.tone} ms-2`}>
              {subjectKind === "search_key_term"
                ? t("specialist.feeder.term.phase", "Término Clave")
                : t(casePhaseMeta.key, casePhaseMeta.fallback)}
            </span>
          </p>
        )}
        {multipleSelected && (
          <div className="mb-3">
            <p className="small text-muted mb-2">
              {t(
                "general.flow-engine.mass-selected",
                "Operación masiva: {{count}} registros seleccionados.",
                { count: massTargets.length }
              )}
            </p>
            {massStateNotice}
          </div>
        )}

        {summaryBlock}
        {actionsBlock}

        {showSingleRecordHint && (
          <p className="small text-muted mt-2 mb-0">
            {t(
              "general.flow-engine.history.multi-select-hint",
              "Con varios registros seleccionados, usa el semáforo de cada fila para ver su historial individual."
            )}
          </p>
        )}

        {actionModal}

        <FlowRecordHistoryModal
          show={isHistoryOpen}
          onHide={() => setIsHistoryOpen(false)}
          record={record}
          moduleId={moduleId}
          moduleProcess={moduleProcess}
          subjectKind={subjectKind}
        />
      </div>
    </div>
  );
};

FlowEnginePanel.propTypes = {
  record: PropTypes.object,
  moduleId: PropTypes.number.isRequired,
  moduleProcess: PropTypes.string.isRequired,
  moduleName: PropTypes.string,
  roleId: PropTypes.number,
  roleCode: PropTypes.string,
  roleName: PropTypes.string,
  canOperate: PropTypes.bool,
  selectedRecords: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object])
  ),
  onAfterAction: PropTypes.func,
  layoutMode: PropTypes.oneOf(["list", "edit"]),
  showActions: PropTypes.bool,
  showSummary: PropTypes.bool,
  showHistoryButton: PropTypes.bool,
  compact: PropTypes.bool,
  poolSurface: PropTypes.string,
  activeCatalogRoleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  activeRoleKey: PropTypes.string,
  subjectKind: PropTypes.string
};

FlowEnginePanel.defaultProps = {
  moduleName: "",
  selectedRecords: [],
  onAfterAction: undefined,
  canOperate: true,
  layoutMode: "list",
  showActions: true,
  showSummary: false,
  showHistoryButton: true,
  compact: false
};

export default FlowEnginePanel;
